import { useState, useEffect } from 'react';

function _classPrivateFieldInitSpec(e, t, a) { _checkPrivateRedeclaration(e, t), t.set(e, a); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _classPrivateFieldGet(s, a) { return s.get(_assertClassBrand(s, a)); }
function _classPrivateFieldSet(s, a, r) { return s.set(_assertClassBrand(s, a), r), r; }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
const defaultStormConfig = {
  evolutionTheme: false
};
var _zombieVampireWerewolfGhost = /*#__PURE__*/new WeakMap();
var _config = /*#__PURE__*/new WeakMap();
var _listeners = /*#__PURE__*/new WeakMap();
/**
 * Class that stores the configuration values for Storm. It is used by ConfigObserver
 * and is initialized using either default values or values retrieved from the window.
 *
 * When ConfigObservers are rendered in the DOM, their config state setter functions
 * are added as functions to be called when config properties change.
 */
class StormConfig {
  constructor(configArray) {
    /**
     * There is a case where multiple copies of Storm can be loaded on the page.  Each
     * copy of Storm has its own StormConfig class which caused 'instanceof' checks to
     * fail. Instead, we check for this meaningless class member. Do not touch or the
     * spooky monsters will get you.
     */
    _classPrivateFieldInitSpec(this, _zombieVampireWerewolfGhost, void 0);
    // The Storm config object
    _classPrivateFieldInitSpec(this, _config, void 0);
    // An array of config state setter functions
    _classPrivateFieldInitSpec(this, _listeners, void 0);
    _classPrivateFieldSet(_zombieVampireWerewolfGhost, this, 'zombieVampireWerewolfGhost');
    _classPrivateFieldSet(_listeners, this, []);
    _classPrivateFieldSet(_config, this, configArray.reduce((acc, current) => ({
      ...acc,
      ...current
    }), {
      ...defaultStormConfig
    }));
  }

  // Getter for spooky monsters
  get spookyMonstersInside() {
    return _classPrivateFieldGet(_zombieVampireWerewolfGhost, this);
  }

  // Getter for the config object.
  get value() {
    return _classPrivateFieldGet(_config, this);
  }

  // Adds a config state setter to listeners so that it is called when config
  // settings are pushed to the stormConfig.
  addChangeListener(listener) {
    if (_classPrivateFieldGet(_listeners, this).indexOf(listener) === -1) {
      _classPrivateFieldGet(_listeners, this).push(listener);
    }
  }

  // Removes a config state setter from listeners so that it isn't called when config
  // settings are pushed to the stormConfig.
  removeChangeListener(listener) {
    const listenerIdx = _classPrivateFieldGet(_listeners, this).indexOf(listener);
    if (listenerIdx !== -1) {
      _classPrivateFieldGet(_listeners, this).splice(listenerIdx, 1);
    }
  }

  // This function is only used by builders who wish to test Storm config settings using
  // the browser development tools. (e.g. window.stormConfig.push({ evolutionTheme: true });)
  // When push is called, new config values are spread onto the old config and every ConfigObserver
  // in the document will update to use the new values.
  push(config) {
    _classPrivateFieldSet(_config, this, {
      ..._classPrivateFieldGet(_config, this),
      ...config
    });
    _classPrivateFieldGet(_listeners, this).forEach(listener => {
      listener(_classPrivateFieldGet(_config, this));
    });
  }
}
const isStormConfig = config =>
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
config !== null && typeof config === 'object' && 'spookyMonstersInside' in config;

/**
 * Higher order component that manages the state of the Storm config. It looks for
 * configuration values set on the window and converts those settings to state which
 * affect how Storm components render in the DOM.
 *
 * Console and DSP navigation will add storm configuration values on the window. This
 * component detects those values and translates them to state.
 */
const ConfigObserver = ({
  children
}) => {
  // Initialize a new storm config if we haven't already and if we are rendering in an
  // environment that has a global window. It is possible that a StormConfig has already
  // been initialized (i.e. nested ThemeProviders).
  if (typeof window !== 'undefined' && !isStormConfig(window.stormConfig)) {
    window.stormConfig = new StormConfig(Array.isArray(window.stormConfig) ? window.stormConfig : [defaultStormConfig]);
  }

  // Hold the state of the storm Config on the window.
  const [configState, setConfigState] = useState(typeof window !== 'undefined' && isStormConfig(window.stormConfig) ? window.stormConfig.value : defaultStormConfig);
  useEffect(() => {
    // To support builders being able to update Storm config values in the console of the
    // browser developer tools, we add the config state setter. This tells the StormConfig
    // to update this config when a config setting is changed.
    if (typeof window !== 'undefined' && isStormConfig(window.stormConfig)) {
      window.stormConfig.addChangeListener(setConfigState);
    }

    // Cleanup remove the config state setter from those which will be updated by StormConfig.
    return () => {
      if (typeof window !== 'undefined' && isStormConfig(window.stormConfig)) {
        window.stormConfig.removeChangeListener(setConfigState);
      }
    };
  }, []);
  return children(configState);
};

export { ConfigObserver as C };
//# sourceMappingURL=ConfigObserver-xpKlUycK.js.map
